import { text } from '@clack/prompts'
import as from 'ansis'
import { Command } from 'commander'

import { ConfigVersion } from '@scalar-org/entities/project'

import { readAndParseConfig } from '@/domains/project/check-config'
import { CONFIG_FILE_DEFAULT, findConfig } from '@/domains/project/helpers'
import { output } from '@/helpers/std-out'

import { previewV2Project } from './v2'

export const PreviewCommand = () => {
  const cmd = new Command('preview')

  cmd.description('Preview scalar guides')
  cmd.argument(
    '[config]',
    'Path to the Scalar configuration file (usually `scalar.config.json5` or `scalar.config.json`)',
  )
  cmd.option(
    '-p, --port [port]',
    'port to run the server on. If the port is not available, it will select another one.',
    '7970',
  )
  cmd.option(
    '-h, --host [host]',
    'Specify which IP addresses the server should listen on.',
  )
  cmd.option(
    '-L, --log-level <level>',
    'Set the log level (debug, info, warn, error, trace)',
    'info',
  )
  cmd.option(
    '-F, --log-formatting <formatting>',
    'Set the log formatting (pretty|none). Defaults to pretty formatting outside of CI.',
    'pretty',
  )
  cmd.option('-N, --no-open', 'Do not open the browser automatically', true)
  cmd.action(async (__configPath, options) => {
    const _port = Number.parseInt(options.port || '7970', 10)

    if (Number.isNaN(_port)) {
      return output
        .error()
        .title('Invalid port')
        .message('Please provide a valid port number')
        .exit('error')
    }
    if (_port < 0 || _port > 65535) {
      return output
        .error()
        .title('Invalid port')
        .message('Port number must be between 0 and 65535')
        .exit('error')
    }

    const configPath =
      __configPath ||
      findConfig() ||
      (await text({
        message: 'Where is your configuration file located?',
        defaultValue: CONFIG_FILE_DEFAULT,
        placeholder: CONFIG_FILE_DEFAULT,
      }))

    // Validate the configuration file first
    const parseConfigResult = readAndParseConfig(configPath)

    if (!parseConfigResult.success) {
      const error = parseConfigResult.error
      if (error.type === 'file-not-found') {
        if (Number.isNaN(_port)) {
          return output
            .error()
            .title('Invalid port')
            .message('Please provide a valid port number')
            .exit('error')
        }
        if (_port < 0 || _port > 65535) {
          return output
            .error()
            .title('Invalid port')
            .message('Port number must be between 0 and 65535')
            .exit('error')
        }
      }

      if (error.type === 'parse-error') {
        return output
          .error()
          .title('Invalid configuration file')
          .message('Could not parse the Scalar Configuration file:')
          .message(error.filePath)
          .exit('error')
      }

      if (error.type === 'validation-error') {
        output
          .error()
          .title('The given Scalar Configuration is invalid.')
          .message(error.filePath)
          .print()

        if (error.issues) {
          output
            .error()
            .table(
              error.issues.map((issue) => [
                as.yellow(`${issue.path.join('/').trim() || 'root'}:`),
                issue.message,
              ]),
              { padding: 2 },
            )
            .print()
        }
      }

      return output.error().exit('error')
    }

    // Configuration is valid, show success message
    if (parseConfigResult.success) {
      output
        .info()
        .line(
          `${as.green('[SUCCESS]')} ${as.green('Configuration is valid:')} ${as.green(parseConfigResult.filePath)}`,
        )
        .print()
    }

    const { config } = parseConfigResult
    if (config.version === ConfigVersion.V1) {
      return output
        .error()
        .title('V1 Preview is no longer supported.')
        .message('Please upgrade your project with `scalar project upgrade`')
        .exit('error')
    }

    if (config.version === ConfigVersion.V2) {
      await previewV2Project({
        logLevel: options.logLevel,
        configPath,
        config: config,
        port: _port,
        host: options.host,
      })
      return
    }
  })

  return cmd
}
